import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSubscriptionModal } from '../redux/appConfigurator/action';
import { setSubscription } from '../redux/subscription/actions';
import {
    creatorEffectiveEntitlementsSelector,
    fetchingUserSubscriptionSelector,
    hasCreatorAutoUploadEntitlementSelector,
    hasInstagramAutomationEntitlementSelector,
    userSubscriptionLoadedSelector,
    userSubscriptionSelector,
} from '../pages/EsSettings/selector';
import {
    hasHostAutoUploadEntitlementSelector,
    hasHostInstagramAutomationEntitlementSelector,
} from '../redux/campaignHostSubscription/selectors';
import type { CreatorEntitlementKey } from '../utils/planEntitlements';
import { getJWtDetails } from '../utils/AppUtils';

/** Entitlements needed for direct social upload + Instagram automation upgrades. */
export const SOCIAL_UPLOAD_UPGRADE_ENTITLEMENTS: CreatorEntitlementKey[] = ['auto_upload', 'instagram_automation'];

/** Plans-modal headline when a feature gate opens upgrade (not campaign apply). */
export function resolveFeatureGateHeadline(keys?: CreatorEntitlementKey[] | null): string | null {
    if (!keys?.length) return null;
    const set = new Set(keys);
    if (set.has('auto_upload') || (set.has('instagram_automation') && keys.length > 1)) {
        return 'Post Automation requires Pro';
    }
    if (set.has('instagram_automation')) {
        return 'Instagram Automation requires Pro';
    }
    return 'This feature requires Pro';
}

/**
 * Creator + host entitlement gates for social upload / Instagram automation.
 * Loads creator subscription from GET /subscribe/get-my-plan when missing.
 */
export function usePlanEntitlementGates() {
    const dispatch = useDispatch();
    const { isSignedIn } = getJWtDetails();
    const subscription = useSelector(userSubscriptionSelector);
    const fetchingSubscription = useSelector(fetchingUserSubscriptionSelector);
    const subscriptionLoaded = useSelector(userSubscriptionLoadedSelector);
    const entitlements = useSelector(creatorEffectiveEntitlementsSelector);
    const hasCreatorInstagramAutomation = useSelector(hasInstagramAutomationEntitlementSelector);
    const hasCreatorAutoUpload = useSelector(hasCreatorAutoUploadEntitlementSelector);
    const hasHostAutoUpload = useSelector(hasHostAutoUploadEntitlementSelector);
    const hasHostInstagramAutomation = useSelector(hasHostInstagramAutomationEntitlementSelector);

    useEffect(() => {
        if (!isSignedIn) return;
        // Users with no plan still get a successful get-my-plan with empty data.
        // Without a loaded flag we re-fetch forever (subscription stays undefined).
        if (subscription || fetchingSubscription || subscriptionLoaded) return;
        dispatch(setSubscription());
    }, [dispatch, isSignedIn, subscription, fetchingSubscription, subscriptionLoaded]);

    const hasInstagramAutomation =
        hasCreatorInstagramAutomation || hasHostInstagramAutomation || entitlements.instagram_automation === true;
    const canDirectUpload =
        hasCreatorAutoUpload || hasHostAutoUpload || hasInstagramAutomation || entitlements.auto_upload === true;

    const openSubscriptionModal = useCallback(
        (requireAnyEntitlement?: CreatorEntitlementKey[]) => {
            if (requireAnyEntitlement?.length) {
                dispatch(
                    toggleSubscriptionModal({
                        open: true,
                        requireAnyEntitlement,
                    }),
                );
                return;
            }
            dispatch(toggleSubscriptionModal(true));
        },
        [dispatch],
    );

    /**
     * @returns true if the caller should proceed (open upload/automation UI)
     */
    const requireUploadEntitlement = useCallback((): boolean => {
        if (canDirectUpload) return true;
        openSubscriptionModal(SOCIAL_UPLOAD_UPGRADE_ENTITLEMENTS);
        return false;
    }, [canDirectUpload, openSubscriptionModal]);

    /**
     * @returns true if Instagram comment automation may be enabled
     */
    const requireInstagramAutomationEntitlement = useCallback((): boolean => {
        if (hasInstagramAutomation) return true;
        openSubscriptionModal(['instagram_automation']);
        return false;
    }, [hasInstagramAutomation, openSubscriptionModal]);

    return {
        hasInstagramAutomation,
        hasCreatorAutoUpload,
        hasHostAutoUpload,
        canDirectUpload,
        entitlements,
        subscriptionLoaded: subscriptionLoaded || !isSignedIn,
        fetchingSubscription,
        openSubscriptionModal,
        requireUploadEntitlement,
        requireInstagramAutomationEntitlement,
    };
}
